import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { apiGet, apiPost, setOrgView } from "./api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);      // null = checking or logged-out
  const [tenant, setTenant] = useState(null);  // active-org context (is_demo)
  const [ready, setReady] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const me = await apiGet("/auth/me");
      setUser(me);
      try { setTenant(await apiGet("/tenant/context")); } catch { setTenant(null); }
    } catch {
      setUser(null);
      setTenant(null);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async ({ email, password, captcha_id, captcha_answer }) => {
    const data = await apiPost("/auth/login", { email, password, captcha_id, captcha_answer });
    if (data.requires_2fa) return { requires_2fa: true, temp_token: data.temp_token };
    setUser(data.user);
    try { setTenant(await apiGet("/tenant/context")); } catch { setTenant(null); }
    return { requires_2fa: false };
  };

  const verify2fa = async ({ temp_token, code }) => {
    const data = await apiPost("/auth/verify-2fa", { temp_token, code });
    setUser(data.user);
    try { setTenant(await apiGet("/tenant/context")); } catch { setTenant(null); }
    return data.user;
  };

  const logout = async () => {
    try { await apiPost("/auth/logout"); } catch {}
    setOrgView("");
    setUser(null);
    setTenant(null);
  };

  const changePassword = async ({ current_password, new_password }) => {
    await apiPost("/auth/change-password", { current_password, new_password });
    await refresh();
  };

  return (
    <AuthCtx.Provider value={{ user, tenant, ready, login, verify2fa, logout, refresh, changePassword }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  const v = useContext(AuthCtx);
  if (!v) throw new Error("useAuth must be inside AuthProvider");
  return v;
}
